import { inject, Injectable } from '@angular/core';
import { isType } from '@reconcept-platform/shared/data-access';
import { type LogItemFragmentFragment } from '@reconcept-platform/shared/types';
import { type JSONContent } from '@tiptap/core';
import { map } from 'rxjs/operators';
import { LogApi } from '../../api/log/log.api';
import {
  CardLogLogTypeEnum,
  type EventCardStatusEnum,
  type GoalCardSeverityEnum,
  type GoalCardStatusEnum,
  type MostSevereSeverityEnum,
} from '../../schema/schema-enums';

export enum SubjectLogType {
  cardComment = 'CardComment',
  cardTask = 'CardTask',
  eventCard = 'EventCard',
  goalCard = 'GoalCard',
  user = 'User',
  none = 'None',
}

export interface LogView extends LogItemFragmentFragment {
  logChanges: LogChangesView | undefined;
  createdAt: string;
  initiallyVisible: boolean;
}

export interface LogChangesView extends Partial<Record<LogChangesKeyEnum, { oldValue: any; newValue: any }>> {
  [LogChangesKeyEnum.status]?: {
    oldValue: EventCardStatusEnum | GoalCardStatusEnum;
    newValue: EventCardStatusEnum | GoalCardStatusEnum;
  };
  [LogChangesKeyEnum.severity]?: {
    oldValue: MostSevereSeverityEnum | GoalCardSeverityEnum;
    newValue: MostSevereSeverityEnum | GoalCardSeverityEnum;
  };
  [LogChangesKeyEnum.descriptionJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
  [LogChangesKeyEnum.descriptionPlanJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
  [LogChangesKeyEnum.descriptionDoJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
  [LogChangesKeyEnum.descriptionCheckJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
  [LogChangesKeyEnum.descriptionActJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
  [LogChangesKeyEnum.descriptionCheckIndicatorsJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
  [LogChangesKeyEnum.commentJsonContent]?: {
    oldValue: JSONContent | null;
    newValue: JSONContent | null;
  };
}

export enum LogChangesKeyEnum {
  archivedAt = 'archived_at',
  attachedFiles = 'attached_files',
  cardId = 'card_id',
  comment = 'comment',
  commentJsonContent = 'comment_json_content',
  completedAt = 'completed_at',
  creatorId = 'creator_id',
  description = 'description',
  descriptionAct = 'description_act',
  descriptionActJsonContent = 'description_act_json_content',
  descriptionCheck = 'description_check',
  descriptionCheckIndicators = 'description_check_indicators',
  descriptionCheckIndicatorsJsonContent = 'description_check_indicators_json_content',
  descriptionCheckJsonContent = 'description_check_json_content',
  descriptionDo = 'description_do',
  descriptionDoJsonContent = 'description_do_json_content',
  descriptionJsonContent = 'description_json_content',
  descriptionPlan = 'description_plan',
  descriptionPlanJsonContent = 'description_plan_json_content',
  endDate = 'end_date',
  featured = 'featured',
  order = 'order',
  qualityInstrumentId = 'quality_instrument_id',
  severity = 'severity',
  startDate = 'start_date',
  status = 'status',
  subDomainId = 'sub_domain_id',
  title = 'title',
  type = 'type',
  workspaceId = 'workspace_id',
}

@Injectable({
  providedIn: 'root',
})
export class LogService {
  private logApi = inject(LogApi);

  watchGoalCardLogs(goalCardIds: string[]) {
    return this.logApi.watchGoalCardLogs(goalCardIds).pipe(map((cardLog) => this.mapToLogViews(cardLog)));
  }

  watchEventCardLogs(eventCardIds: string[]) {
    return this.logApi.watchEventCardLogs(eventCardIds).pipe(map((cardLog) => this.mapToLogViews(cardLog)));
  }

  private mapToLogViews(cardLogs: LogItemFragmentFragment[]): LogView[] {
    return cardLogs.map((cardLog) => this.mapToLogView(cardLog));
  }

  private mapToLogView(cardLog: LogItemFragmentFragment): LogView {
    return {
      ...cardLog,
      createdAt: cardLog?.createdAt?.toLocaleString(),
      logChanges: cardLog?.logChanges ? this.mapToLogChangesViews(cardLog.logChanges) : undefined,
      initiallyVisible: this.determineIfLogIsInitiallyVisible(cardLog),
    };
  }

  private mapToLogChangesViews(logChanges: JSON): LogChangesView {
    const logChangesView: LogChangesView = {};

    Object.keys(logChanges).forEach((key) => {
      (logChangesView as any)[key] = {
        oldValue: (logChanges as any)[key][0],
        newValue: (logChanges as any)[key][1],
      };
    });

    return logChangesView;
  }

  private determineIfLogIsInitiallyVisible(cardLog: LogItemFragmentFragment) {
    return cardLog.logType === CardLogLogTypeEnum.DEFAULT_CREATE && isType(cardLog.subject, 'CardComment');
  }
}
